import React from 'react';

import { Color, FontSize, FontWeight } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { placeholdersMap, PLAYERAVATAR } from '../../../utils/placeholders-football';
import { makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import { IconPosition } from '../../molecules/team/team';
import Text, { TextTags } from '../text/text';

import AvatarPlaceholder from './avatar-placeholder';

import './football-avatar.scss';

export interface IFootballAvatarLabel {
  colorLabel?: Color;
  fontSizeLabel?: FontSize;
  fontWeightLabel?: FontWeight;
  tagLabel?: TextTags;
}

export interface IFootballAvatarName {
  colorName?: Color;
  fontSizeName?: FontSize;
  fontWeightName?: FontWeight;
  tagName?: TextTags;
}
export interface IFootballAvatar extends IFootballAvatarLabel, IFootballAvatarName, IComponent {
  borderColor?: Color;
  borderThickness?: Number;
  imagePosition?: IconPosition;
  isCircled?: boolean;
  label?: string;
  name?: string;
  url?: string;
  images: string[];
  placeholder?: string;
}

const classNames = (isCircled: boolean, borderColor: Color, imagePosition: IconPosition, borderThickness?: Number) =>
  makeClassNames({
    avatar: ['football-avatar', imagePosition && `football-avatar--image-${imagePosition}`],
    avatarImage: [
      'football-avatar--image',
      isCircled && `football-avatar--image-circled`,
      borderColor && `football-avatar--image-border-${borderColor}`,
      (borderThickness || borderThickness === 0) && `football-avatar--image-border-w${borderThickness}`,
    ],
    avatarInfo: 'football-avatar--info',
    avatarLabel: 'football-avatar--info-label',
    avatarName: 'football-avatar--info-name',
  });

const Avatar = ({
  /* istanbul ignore next: default value */ borderColor = 'red-light',
  /* istanbul ignore next: default value */ colorLabel = 'blue-light',
  /* istanbul ignore next: default value */ colorName = 'blue',
  /* istanbul ignore next: default value */ fontSizeLabel = '12',
  /* istanbul ignore next: default value */ fontSizeName = '12',
  /* istanbul ignore next: default value */ fontWeightLabel = '400',
  /* istanbul ignore next: default value */ fontWeightName = '500',
  /* istanbul ignore next: default value */ imagePosition = 'left',
  /* istanbul ignore next: default value */ isCircled = false,
  /* istanbul ignore next: default value */ placeholder = placeholdersMap[PLAYERAVATAR],
  /* istanbul ignore next: default value */ tagLabel = 'span',
  /* istanbul ignore next: default value */ tagName = 'p',
  /* istanbul ignore next: default value */ trackingData = {},
  borderThickness,
  images,
  label,
  name,
  url,
}: IFootballAvatar) => {
  const cn = classNames(isCircled, borderColor, imagePosition, borderThickness);
  const imageSize = 90;

  const avatarImage = (
    <div className={cn.avatarImage}>
      <AvatarPlaceholder
        role="presentation"
        loading="lazy"
        title={name}
        images={images}
        placeholder={placeholder}
        size={imageSize}
      />
    </div>
  );

  const avatarInfo = (
    <div className={cn.avatarInfo}>
      {label && (
        <Text
          className={cn.avatarLabel}
          tag={tagLabel}
          fontSize={fontSizeLabel}
          fontWeight={fontWeightLabel}
          color={colorLabel}
        >
          {label}
        </Text>
      )}
      {name && (
        <Text
          className={cn.avatarName}
          tag={tagName}
          fontSize={fontSizeName}
          fontWeight={fontWeightName}
          color={colorName}
        >
          {name}
        </Text>
      )}
    </div>
  );

  return url ? (
    <a className={cn.avatar} href={url} {...objectToDataAttributes(trackingData)}>
      {avatarImage}
      {avatarInfo}
    </a>
  ) : (
    <div className={cn.avatar}>
      {avatarImage}
      {avatarInfo}
    </div>
  );
};

export default Avatar;
